import React from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  DollarSign,
  Shield,
  Clock,
  Zap,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import { useBookingModal } from "../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const BenefitsSection: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { openModal } = useBookingModal();

  const benefits = [
    {
      icon: DollarSign,
      title: t("1019", "Fixed Price"),
      description: t(
        "1020",
        "Enjoy peace of mind with our fixed rates, ensuring you always get the best value."
      ),
      gradient: "from-accent to-primary-light",
      bgGradient: "from-accent/5 to-primary-light/5",
      features: [
        t("1021", "No hidden fees"),
        t("1022", "Transparent pricing"),
        t("1023", "Value guarantee"),
      ],
    },
    {
      icon: Shield,
      title: t("1024", "Safety Assurance"),
      description: t(
        "1025",
        "Our professionally trained drivers are equipped to handle any situation, prioritizing your safety at every step."
      ),
      gradient: "from-primary to-primary-dark",
      bgGradient: "from-primary/5 to-primary-dark/5",
      features: [
        t("1026", "Licensed drivers"),
        t("1027", "Insured vehicles"),
        t("1028", "24/7 monitoring"),
      ],
    },
    {
      icon: Clock,
      title: t("1029", "Rapid Pickups"),
      description: t(
        "1030",
        "At TaxiSchipholAirport, we're committed to both competitive pricing and exceptional service. Count on us for swift pickups."
      ),
      gradient: "from-primary-light to-primary",
      bgGradient: "from-primary-light/5 to-primary/5",
      features: [
        t("1031", "5-minute response"),
        t("1032", "Real-time tracking"),
        t("1033", "Punctual service"),
      ],
    },
    {
      icon: Zap,
      title: t("1034", "Efficient Rides"),
      description: t(
        "1035",
        "We believe in fast, efficient rides without sacrificing safety. Experience reliable, timely transport with TaxiSchipholAirport."
      ),
      gradient: "from-warning to-primary",
      bgGradient: "from-warning/5 to-primary/5",
      features: [
        t("1036", "Optimized routes"),
        t("1037", "Traffic monitoring"),
        t("1038", "Time efficiency"),
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  return (
    <section ref={ref} className="py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/20 rounded-full opacity-30 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-warning/20 rounded-full opacity-30 blur-3xl" />

        {/* Floating Shapes */}
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-4 h-4 bg-linear-to-r from-primary/20 to-primary-light/20 rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, 10, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-20"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
          >
            <Sparkles className="h-4 w-4" />
            <span>{t("1039", "Why Choose Us")}</span>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-6xl font-bold text-white mb-8"
          >
            {t("1040", "Unmatched")}{" "}
            <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
              {t("1041", "Benefits")}
            </span>
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            {t(
              "1042",
              "Experience the difference with TaxiSchipholAirport's premium transportation services"
            )}
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="w-32 h-1 bg-linear-to-r from-primary to-primary-light mx-auto rounded-full mt-8"
          />
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {benefits.map((benefit) => (
            <motion.div
              key={benefit.title}
              variants={itemVariants}
              whileHover={{ y: -15, scale: 1.02 }}
              className="group relative"
            >
              {/* Main Card */}
              <div className="relative bg-gray-800/50 backdrop-blur-xs rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-700 overflow-hidden h-full flex flex-col">
                {/* Background Gradient on Hover */}
                <motion.div
                  className={`absolute inset-0 bg-linear-to-br ${benefit.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                />

                {/* Icon */}
                <motion.div
                  className={`relative inline-flex items-center justify-center w-20 h-20 bg-linear-to-r ${benefit.gradient} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <benefit.icon className="h-10 w-10 text-white" />
                </motion.div>

                {/* Content */}
                <div className="relative flex-1 flex flex-col">
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-linear-to-r group-hover:from-primary group-hover:to-primary-light group-hover:bg-clip-text transition-all duration-300">
                    {benefit.title}
                  </h3>

                  <p className="text-gray-200 leading-relaxed mb-6 flex-1">
                    {benefit.description}
                  </p>

                  {/* Feature List */}
                  <div className="space-y-2 mt-auto">
                    {benefit.features.map((feature) => (
                      <motion.div
                        key={feature}
                        initial={{ opacity: 0, x: -20 }}
                        animate={
                          isInView
                            ? { opacity: 1, x: 0 }
                            : { opacity: 0, x: -20 }
                        }
                        transition={{
                          duration: 0.5,
                        }}
                        className="flex items-center space-x-2 text-sm text-gray-300"
                      >
                        <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Decorative Elements */}
                <motion.div
                  className="absolute top-4 right-4 w-3 h-3 bg-linear-to-r from-blue-400 to-purple-400 rounded-full opacity-30"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />

                <motion.div
                  className="absolute bottom-4 left-4 w-2 h-2 bg-linear-to-r from-amber-400 to-orange-400 rounded-full opacity-30"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                  }}
                />
              </div>

              {/* Floating Badge */}
              <motion.div
                className="absolute -top-3 -right-3 bg-linear-to-r from-amber-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg"
                animate={{
                  y: [0, -5, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              >
                {t("1043", "Premium")}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-20"
        >
          <div className="bg-linear-to-r from-secondary via-primary-dark to-secondary rounded-3xl p-12 text-white relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ff5700\' fill-opacity=\'0.1\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] bg-repeat"></div>
            </div>

            <div className="relative">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                {t("1044", "Ready to Experience Premium Transportation?")}
              </h3>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                {t(
                  "1045",
                  "Join thousands of satisfied customers who trust TaxiSchipholAirport for their transportation needs"
                )}
              </p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={openModal}
                className="bg-linear-to-r from-warning to-primary hover:from-primary hover:to-primary-dark text-white px-8 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t("1046", "Book Your Premium Ride Now")}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
